import {
  COST_IMAGE_1K,
  COST_IMAGE_2K,
  COST_IMAGE_4K,
  COST_FAST_1K,
  COST_FAST_2K,
  COST_FAST_4K,
} from "../../functions/src/shared/imagePricing";
export {
  COST_IMAGE_1K,
  COST_IMAGE_2K,
  COST_IMAGE_4K,
  COST_FAST_1K,
  COST_FAST_2K,
  COST_FAST_4K,
};
import type { VideoRenderSettings } from "../types";

// Generation quality options (resolution tiers)
export const GENERATION_QUALITIES = ["1K", "2K", "4K"] as const;
export type GenerationQuality = (typeof GENERATION_QUALITIES)[number];

// PRICING CONSTANTS
export const COST_BG_REMOVE = 1.0; // Freistellen - Professional background removal
export const COST_EDITS = 2.0; // Edits - Manual touch-ups
export const COST_RECOLOR = 2.0; // Umfärben - Color variation
export const COST_UPSCALE = 1.0; // Upscale (SeedVR2, standard) - flat per run
export const COST_UPSCALE_TOPAZ_MIN = 2.0; // Upscale (Topaz, premium) - entry tier
export const COST_DIMENSION = 1.0; // Dimensional Drawing - initial creation only; re-edits are free
export const COST_SET_IMAGE = 1.0; // Set image - initial creation only; re-edits are free

export const CREDIT_VALUE_CENTS = 25;
export const VIDEO_COST_MARKUP_MULTIPLIER = 2.3;

type VideoPricingTable = Record<
  VideoRenderSettings["modelId"],
  Record<
    VideoRenderSettings["resolution"],
    Record<VideoRenderSettings["duration"], number>
  >
>;

export const VIDEO_API_COST_CENTS = {
  "veo-3.1-fast-generate-001": {
    "720p": { 4: 40, 6: 60, 8: 80 },
    "1080p": { 4: 40, 6: 60, 8: 80 },
  },
  "veo-3.1-generate-001": {
    "720p": { 4: 80, 6: 120, 8: 160 },
    "1080p": { 4: 80, 6: 120, 8: 160 },
  },
} as const satisfies VideoPricingTable;

export function getVideoGenerationCreditCost(
  settings: Pick<VideoRenderSettings, "modelId" | "duration" | "resolution">
): number {
  const apiCostCents =
    VIDEO_API_COST_CENTS[settings.modelId][settings.resolution][
      settings.duration
    ];
  return Math.ceil(
    (apiCostCents * VIDEO_COST_MARKUP_MULTIPLIER) / CREDIT_VALUE_CENTS
  );
}

/**
 * Topaz is billed by output size, so the tiering is passed through to the
 * user. Bounds are output megapixels. Mirrors `functions/src/ai/types.ts`.
 */
export const TOPAZ_UPSCALE_COST_TIERS: ReadonlyArray<{
  maxMegapixels: number;
  cost: number;
}> = [
  { maxMegapixels: 24, cost: COST_UPSCALE_TOPAZ_MIN },
  { maxMegapixels: 48, cost: 3.0 },
  { maxMegapixels: 96, cost: 4.0 },
];

/**
 * The tiers a user can actually reach, given the current output cap, with
 * each bound clamped to that cap. Tiers that start above the cap are dropped
 * entirely — advertising a price nobody can trigger reads as a bait price.
 *
 * Kept dynamic rather than trimming the table itself, so moving the cap needs
 * no pricing edit.
 *
 * @param maxMegapixels The active output ceiling (MAX_OUTPUT_MEGAPIXELS).
 */
export function getReachableUpscaleTiers(
  maxMegapixels: number
): ReadonlyArray<{ maxMegapixels: number; cost: number }> {
  return TOPAZ_UPSCALE_COST_TIERS.filter(
    (tier, index) =>
      index === 0 ||
      TOPAZ_UPSCALE_COST_TIERS[index - 1].maxMegapixels < maxMegapixels
  ).map((tier) => ({
    ...tier,
    maxMegapixels: Math.min(tier.maxMegapixels, maxMegapixels),
  }));
}

/**
 * Credit cost of a single upscale. SeedVR is flat; Topaz is tiered by the
 * predicted output size (deterministic from the source dimensions and the
 * factor), so the exact price can be shown before the run.
 */
export function getUpscaleCreditCost(
  upscaler: "seedvr" | "topaz",
  outputMegapixels: number | null | undefined
): number {
  if (upscaler !== "topaz") return COST_UPSCALE;
  if (!outputMegapixels) return COST_UPSCALE_TOPAZ_MIN;
  const tier = TOPAZ_UPSCALE_COST_TIERS.find(
    (t) => outputMegapixels <= t.maxMegapixels
  );
  return (
    tier?.cost ??
    TOPAZ_UPSCALE_COST_TIERS[TOPAZ_UPSCALE_COST_TIERS.length - 1].cost
  );
}

export function formatCreditAmount(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export const COST_VIDEO = getVideoGenerationCreditCost({
  modelId: "veo-3.1-fast-generate-001",
  duration: 4,
  resolution: "720p",
}); // Default video generation
